import { useEffect, useRef, type HTMLAttributes } from 'react';

const INTERACTIVE =
  'a,button,input,textarea,select,audio,video,[contenteditable="true"],[role="button"],[role="menu"]';
const isInteractive = (target: EventTarget | null) =>
  target instanceof Element && Boolean(target.closest(INTERACTIVE));

/**
 * Opens a contextual action surface without intercepting scrolling or native controls.
 * @param onOpen - Opens the owning menu after right-click, keyboard action or a stationary touch hold.
 * @returns DOM handlers for the non-interactive content surrounding the menu trigger.
 */
export function useContextMenuGestures(
  onOpen: () => void
): HTMLAttributes<HTMLDivElement> {
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const origin = useRef<{ x: number; y: number }>();
  const held = useRef(false);
  const open = useRef(onOpen);
  open.current = onOpen;
  const cancel = () => {
    clearTimeout(timer.current);
    timer.current = undefined;
    origin.current = undefined;
  };

  useEffect(() => {
    window.addEventListener('scroll', cancel, true);
    return () => {
      cancel();
      window.removeEventListener('scroll', cancel, true);
    };
  }, []);

  return {
    onContextMenu: (event) => {
      if (isInteractive(event.target)) return;
      event.preventDefault();
      cancel();
      open.current();
    },
    onKeyDown: (event) => {
      if (event.target !== event.currentTarget) return;
      if (
        event.key === 'ContextMenu' ||
        (event.shiftKey && event.key === 'F10')
      ) {
        event.preventDefault();
        open.current();
      }
    },
    onPointerDown: (event) => {
      cancel();
      held.current = false;
      if (
        !event.isPrimary ||
        event.pointerType === 'mouse' ||
        isInteractive(event.target)
      ) {
        return;
      }
      origin.current = { x: event.clientX, y: event.clientY };
      timer.current = setTimeout(() => {
        held.current = true;
        cancel();
        open.current();
      }, 500);
    },
    onPointerMove: (event) => {
      if (
        origin.current &&
        Math.hypot(
          event.clientX - origin.current.x,
          event.clientY - origin.current.y
        ) > 8
      ) {
        cancel();
      }
    },
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onPointerLeave: cancel,
    onClickCapture: (event) => {
      if (
        held.current &&
        event.target instanceof Node &&
        event.currentTarget.contains(event.target)
      ) {
        held.current = false;
        event.preventDefault();
        event.stopPropagation();
      }
    },
  };
}
